import { Component, Injector, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { PostalAddress } from '../../../common/model/postal-address';
import { UserAccountService } from '../../../common/service/user-account.service';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { SelectionItem } from '../../../core/component/select/select.component';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { Command, CommandBase, standardCommand } from '../../../core/model/command';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { LeaseAgreementCategory } from '../../../rent/model/const/enums';
import { Contract } from '../../../rent/model/contract';
import { invoiceCommand } from '../../command/unpaid-invoice';
import { GetInvoiceDeliveryMethodIcon, InvoiceDeliveryMethod, InvoiceDeliveryMethodToString, InvoiceType } from '../../model/const/enum';
import { CreditInvoice } from '../../model/credit-invoice';
import { Invoice } from '../../model/invoice';
import { InvoiceContact } from '../../model/invoice-contact';
import { InvoiceDelivery } from '../../model/invoice-delivery';
import { InvoiceReminder } from '../../model/invoice-reminder';
import { InvoiceFormCommandHandlerResolver } from '../../service/command/invoice-form-command-handler-resolver.service';
import { InvoiceTransactionService } from '../../service/data/invoice-transaction.service';
import { InvoiceService } from '../../service/data/invoice.service';
import { MovePaymentFormComponent } from '../move-payment-form/move-payment-form.component';
import { AllowSort } from '../../../core/service/factory/allow-sort';
import { ColumnDefinitionService } from '../../../rent/service/data/column-definition.service';
import { LogRecord } from '../../../common/model/log-record';
import { InvoiceLogService } from '../../service/data/invoice-log.service';
import { InvoiceVerificationLogService } from '../../service/data/invoice-verification-log.service';
import { InvoiceDocument } from '../../model/invoice-document';
import { InvoiceRow } from '../../model/invoice-row';
import { Money } from '../../../core/model/money';
import { InvoiceRowListContext } from '../invoice-row-list/invoice-row-list.component';
import { InvoiceAmountService } from '../../service/invoice-amount.service';
import { InvoiceRecipientRecentLeaseAgreement } from '../../model/invoice-recipient-recent-lease-agreement';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { DimensionLabels, defaultDimensionLabels } from '../../../accounting/model/dimension-labels';
import { DocumentTypeMimeTypes } from '../../../core/model/const/document-type-mime-types';

@Component({
    selector: 'app-invoice-form',
    templateUrl: './invoice-form.component.html',
    styleUrls: ['./invoice-form.component.scss'],
    providers: [DeleteFormCommandHandler, InvoiceFormCommandHandlerResolver, { provide: DataService, useExisting: InvoiceService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InvoiceFormComponent extends FormComponentBase<Invoice> {
  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    private _accountService: UserAccountService,
    activatedRoute: ActivatedRoute,
    dataService: InvoiceService,
    private _nativeWindow: NativeWindowService,
    private _windowService: ActionWindowService,
    private _injector: Injector,
    commandDispatcher: CommandDispatcher,
    handlerResolver: InvoiceFormCommandHandlerResolver,
    factory: ColumnDefinitionFactory,
    messageService: MessageService,
    navigation: NavigationService,
    private _transactionService: InvoiceTransactionService,
    columnDefinitionService: ColumnDefinitionService,
    private logService: InvoiceLogService,
    private verificationLogService: InvoiceVerificationLogService,
    private invoiceAmountService: InvoiceAmountService,
    private readonly _dimensionLabelsService: DimensionLabelsService
  ) {
    super(activatedRoute, dataService, messageService, navigation, commandDispatcher, handlerResolver);

    this._dataService = dataService;
    this.addMenuItemDefinition('INVOICE.COMMAND.GETPDF', invoiceCommand.getpdf);
    this.addMenuItemDefinition('INVOICE.COMMAND.GETPDF_WITH_REGULATIONS', invoiceCommand.getpdfWithRegulations);

    this.AddSubscription(
      this._accountService.current.subscribe(x => {
        if (x.hasAccess(ModuleNames.Invoicing, Features.SendForDebtCollection))
          this.addMenuItemDefinition('INVOICE.COMMAND.DEBT_COLLECTION', invoiceCommand.debtCollection);

        if (x.hasAccess(ModuleNames.Invoicing, Features.CreditInvoices))
          this.addMenuItemDefinition('INVOICE.COMMAND.CREDIT', invoiceCommand.credit);

        this.canManageInvoices = x.hasAccess(ModuleNames.Invoicing, Features.ManageInvoices);
        if (this.canManageInvoices) {
          this.addMenuItemDefinition('INVOICE.COMMAND.SEND', invoiceCommand.send);
          this.addMenuItemDefinition('INVOICE.COMMAND.REMINDER', invoiceCommand.createReminder);
          this.addMenuItemDefinition('INVOICE.COMMAND.REGISTER_PAYMENT', invoiceCommand.registerPayment);
          this.addMenuItemDefinition('INVOICE.COMMAND.REGULATE_FROM_BALANCE', invoiceCommand.regulateFromBalance);
          this.addMenuItemDefinition('INVOICE.COMMAND.REGISTER_CUSTOMER_LOSS', invoiceCommand.customerLoss);
        }

        if (x.hasAccess(ModuleNames.Invoicing, Features.GrantDeferment))
          this.addMenuItemDefinition('INVOICE.COMMAND.EDIT_DEFERMENT_DATE', invoiceCommand.setDefermentDate);
      })
    );

    this.columnAccountingsDefinition = [
      factory.create('VERIFICATION.NUMBER', 'verificationNumber', { allowSort: AllowSort.No }),
      factory.create('VERIFICATION.DESCRIPTION', 'verificationDescription', { allowSort: AllowSort.No }),
      factory.create('VERIFICATION.CREATED_DATE', 'creationDate', { allowSort: AllowSort.No }),
      factory.create('ACCOUNT.ACCOUNTING_DATE', 'businessTransactionDate', { allowSort: AllowSort.No }),
      factory.create('ACCOUNT.NAME', 'accountName', { allowSort: AllowSort.No, visibility: ColumnVisibility.optional }),
      factory.create('ACCOUNT.NUMBER', 'accountNumber', { allowSort: AllowSort.No }),
      factory.createMoney('ACCOUNT.DEBIT', 'debitAmount', { allowSort: AllowSort.No, totalSum: false }),
      factory.createMoney('ACCOUNT.CREDIT', 'creditAmount', { allowSort: AllowSort.No, totalSum: false }),
      factory.create(defaultDimensionLabels.costPlaceLabel, 'costPlace', { allowSort: AllowSort.No }),
      factory.create(defaultDimensionLabels.costBearerLabel, 'costBearer', { allowSort: AllowSort.No, visibility: ColumnVisibility.optional }),
      factory.create(defaultDimensionLabels.counterpartLabel, 'counterpart', { allowSort: AllowSort.No, visibility: ColumnVisibility.optional }),
      factory.create(defaultDimensionLabels.functionLabel, 'function', { allowSort: AllowSort.No, visibility: ColumnVisibility.optional }),
      factory.create(defaultDimensionLabels.projectLabel, 'project', { allowSort: AllowSort.No }),
    ];

    this.columnAccountingBalance = [
      factory.create('ACCOUNT.NAME', 'accountName', { allowSort: AllowSort.No, visibility: ColumnVisibility.optional }),
      factory.create('ACCOUNT.NUMBER', 'accountNumber', { allowSort: AllowSort.No }),
      factory.createMoney('ACCOUNT.DEBIT', 'debitAmount', { allowSort: AllowSort.No, totalSum: false }),
      factory.createMoney('ACCOUNT.CREDIT', 'creditAmount', { allowSort: AllowSort.No, totalSum: false }),
      factory.create(defaultDimensionLabels.costPlaceLabel, 'costPlace', { allowSort: AllowSort.No }),
      factory.create(defaultDimensionLabels.costBearerLabel, 'costBearer', { allowSort: AllowSort.No, visibility: ColumnVisibility.optional }),
      factory.create(defaultDimensionLabels.counterpartLabel, 'counterpart', { allowSort: AllowSort.No, visibility: ColumnVisibility.optional }),
      factory.create(defaultDimensionLabels.functionLabel, 'function', { allowSort: AllowSort.No, visibility: ColumnVisibility.optional }),
      factory.create(defaultDimensionLabels.projectLabel, 'project', { allowSort: AllowSort.No }),
    ];

    this.columnDeliveriesDefinition = [
      factory.createDateTime('COMMON.CREATED_DATE', 'createdDate'),
      factory.createDateTime('COMMON.SENT', 'sentDate'),
      factory.create('INVOICE.DELIVERY_METHOD', 'invoiceDeliveryMethodText'),
      factory.create('INVOICE.DELIVERY_ADDRESS', 'deliveryAddress'),
      factory.createTranslation('COMMON.EMAIL_DELIVERY_STATUS_EMAIL', 'deliveryStatusText', {
        visibility: ColumnVisibility.default,
        helpBuilder: (inv: InvoiceDelivery) => inv.deliveryInfo,
      }),
    ];

    this.columnB2CInvoiceImportEvent = [
      factory.create('COMMON.EMAIL_DELIVERY_STATUS', 'statusText'),
      factory.createDateTime('B2C_INVOICE_IMPORT_EVENT.BANK_SENT', 'bankSentDate'),
      factory.createDateTime('B2C_INVOICE_IMPORT_EVENT.BANK_REPLY', 'bankReplyDate'),
      factory.create('B2C_INVOICE_IMPORT_EVENT.REJECT_TEXT', 'rejectText'),
      factory.createDateTime('B2C_INVOICE_IMPORT_EVENT.IMPORT_DATE', 'importDate', ColumnVisibility.optional),
    ];

    this.columnTransactionsDefinition = [
      factory.createDate('ACCOUNT_TRANSACTION.DATE', 'businessTransactionDate'),
      factory.create('ACCOUNT_TRANSACTION.DESCRIPTION', 'verificationDescription'),
      factory.createMoney('COMMON.AMOUNT', 'amount'),
      factory.create('PAYMENT.PAYER_NAME', 'paymentNameAndAddress'),
      factory.create('PAYMENT.REGISTRATION_TYPE', 'registrationTypeText'),
    ];

    this.columnRemindersDefinition = [
      factory.createDateTime('COMMON.CREATED_DATE', 'createdDate'),
      factory.createDateTime('COMMON.SENT', 'sentDate'),
      factory.create('INVOICE.DELIVERY_METHOD', 'reminderDeliveryMethodText'),
      factory.create('COMMON.EMAIL_DELIVERY_STATUS', 'deliveryStatusText', {
        visibility: ColumnVisibility.default,
        helpBuilder: (ir: InvoiceReminder) => ir.emailDeliveryInfo,
      }),
    ];

    this.columnContactsDefinition = [factory.create('TENANT.EDIT_HEADER', 'contactName'), factory.create('COMMON.ROLE', 'roleDescription')];

    this.columnleaseAgreementDefinition = [
      factory.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption'),
      factory.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      factory.create('COMMON.RENTAL_OBJECT', 'rentalObject'),
      factory.create('COMMON.RENTAL_OBJECT_TYPE', 'rentalObjectType'),
      factory.createDate('LEASE_AGREEMENT.FROM_DATE', 'fromDate'),
      factory.createDate('LEASE_AGREEMENT.TO_DATE', 'lastBillingDate'),
      factory.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOne'),
      factory.createMoney('LEASE_AGREEMENT.RENT_PERIOD_AMOUNT_CURRENT', 'currentPeriodRentAmount', ColumnVisibility.optional),
      factory.createMoney('LEASE_AGREEMENT.RENT_PERIOD_AMOUNT_NEXT', 'nextPeriodRentAmount'),
      factory.create('LEASE_AGREEMENT.INVOICE_PERIOD', 'invoicePeriodText', ColumnVisibility.optional),
    ];

    this.columnCreditInvoiceDefinition = [
      factory.create('INVOICE.INVOICE_NUMBER', 'invoiceNumber'),
      factory.create('INVOICE.PERIOD', 'refersToPeriod'),
      factory.createMoney('INVOICE.PREVIEW.AMOUNT', 'netAmount'),
      factory.createMoney('INVOICE.PREVIEW.VAT', 'vatAmount'),
      factory.createMoney('INVOICE.INVOICEAMOUNT', 'totalAmount'),
    ];

    this.columnDirectDebitDefinition = [
      factory.createDate('DIRECT_DEBIT.CREATED_DATE', 'createdDate'),
      factory.createDate('DIRECT_DEBIT.DEDUCTION_DATE', 'deductionDate'),
      factory.createDate('DIRECT_DEBIT.EXPORT_DATE', 'exportDate'),
      factory.createDate('DIRECT_DEBIT.TRANSACTION_DATE', 'transactionDate'),
      factory.create('DIRECT_DEBIT.PAYER_NAME', 'payerNumber'),
      factory.create('INVOICE.RECIPIENT_NAME', 'debtorName'),
      factory.createMoney('DIRECT_DEBIT.AMOUNT', 'amount'),
      factory.createMoney('DIRECT_DEBIT.REMAINING_AMOUNT', 'unPaidAmount'),
    ];

    this.columnDebtCollectionLogDefinition = [
      factory.createDateTime('DIRECT_DEBIT.CREATED_DATE', 'createdAt'),
      factory.create('COMMON.DESCRIPTION', 'description'),
    ];

    this.transactionsMenuDefinition = [
      new MenuItemDefinition('INCORRECT_PAYMENT.VOID', 'voidTransaction'),
      new MenuItemDefinition('INVOICE.PAYMENT.MOVE_TO_BALANCE_MENUITEM', 'moveToBalance'),
    ];

    this.columnDocumentsDefinition = [
      factory.create('DOCUMENT.CAPTION', 'originalName'),
      factory.createTranslation('DOCUMENT.DOCUMENT_TYPE', 'extension', ColumnVisibility.default),
    ];

    this.documentsContextMenuDefinitions = [new MenuItemDefinition('DOWNLOAD_BTN', standardCommand.download)];

    this.logColumnDefinition = columnDefinitionService.create('Log');
    this.verificationLogColumnDefinition = [
      factory.createDateTime('LOG.CREATED_AT', 'createdAt', { allowSort: AllowSort.No }),
      factory.create('VERIFICATION.DESCRIPTION', 'shortText', { allowSort: AllowSort.No }),
      factory.create('LOG.USERNAME', 'userName', { allowSort: AllowSort.No }),
    ];
  }

  transactionsMenuDefinition: MenuItemDefinition[];
  documentsContextMenuDefinitions: MenuItemDefinition[];

  _dataService: InvoiceService;

  columnAccountingsDefinition: ColumnDefinition[];
  columnAccountingBalance: ColumnDefinition[];
  columnDeliveriesDefinition: ColumnDefinition[];
  columnB2CInvoiceImportEvent: ColumnDefinition[];
  columnTransactionsDefinition: ColumnDefinition[];
  columnRemindersDefinition: ColumnDefinition[];
  columnContactsDefinition: ColumnDefinition[];
  columnleaseAgreementDefinition: ColumnDefinition[];
  columnCreditInvoiceDefinition: ColumnDefinition[];
  columnDirectDebitDefinition: ColumnDefinition[];
  columnDebtCollectionLogDefinition: ColumnDefinition[];
  columnDocumentsDefinition: ColumnDefinition[];
  logColumnDefinition: ColumnDefinition[];
  verificationLogColumnDefinition: ColumnDefinition[];
  logs: LogRecord[] = [];
  verificationLogs: LogRecord[] = [];
  private logLoadSubscription?: Subscription;
  private verificationLogLoadSubscription?: Subscription;
  documents: InvoiceDocument[] = [];
  appendixMimeTypes: string[] = DocumentTypeMimeTypes.PDF();

  invoiceDeliveryMethods: SelectionItem[] = [
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Mail), value: InvoiceDeliveryMethod.Mail, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Mail) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Manual), value: InvoiceDeliveryMethod.Manual, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Manual) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithPDF), value: InvoiceDeliveryMethod.EmailWithPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithPDF) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithoutPDF), value: InvoiceDeliveryMethod.EmailWithoutPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithoutPDF) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EInvoiceB2B), value: InvoiceDeliveryMethod.EInvoiceB2B, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EInvoiceB2B) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.NoDelivery), value: InvoiceDeliveryMethod.NoDelivery, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.NoDelivery) },
  ];

  hasInvoiceRowsChanged = false;

  protected createModel(): Invoice {
    return new Invoice();
  }

  canExecute(command: CommandBase): boolean {
    switch (command.id) {
      case 'voidTransaction':
        return true;

      case 'moveToBalance':
        return command instanceof Command && command.context.amount.amount > 0;

      case standardCommand.download:
        return command instanceof Command && command.context;
      default:
        return super.canExecute(command);
    }
  }

  execute(command: CommandBase): void {
    const externalBlockedCommands = [
      'voidTransaction',
      'moveToBalance',
      invoiceCommand.send,
      invoiceCommand.createReminder,
      invoiceCommand.registerPayment,
      invoiceCommand.regulateFromBalance,
      invoiceCommand.customerLoss,
      invoiceCommand.debtCollection,
      invoiceCommand.setDefermentDate,
    ];
    if (this.dataContext.isExternalAccountsReceivable && externalBlockedCommands.includes(command.id)) {
      this.messageService.showMessage('INVOICE.EXTERNAL_ACCOUNTS_RECEIVABLE.BLOCKED');
      return;
    }

    switch (command.id) {
      case 'voidTransaction':
        this.AddSubscription(
          this._transactionService.void((<Command>command).context.id).subscribe(result => this.messageService.showSuccess())
        );
        break;

      case 'moveToBalance':
        this.AddSubscription(
          this._windowService
            .open(MovePaymentFormComponent, this._injector, null, { transaction: (command as Command).context, invoice: this.dataContext })
            .subscribe(model => {
              this.navigation.reloadCurrentRoute();
            })
        );
        break;

      case standardCommand.download:
        this.AddSubscription(
          this._dataService.getDocument((<Command>command).context.connectionId).subscribe(file => {
            this._nativeWindow.save(file);
          })
        );
        break;

      default:
        return super.execute(command);
    }
  }

  protected executedWithResult(result: boolean, command: CommandBase): void {
    if (result && command.id === invoiceCommand.registerPayment && command instanceof Command) {
      const remainingAmount = (command.context as Invoice).remainingAmount;
      this.dataContext.remainingAmount = remainingAmount;
      this.form.controls['remainingAmount'].setValue(remainingAmount);
      this.commandContext = null;
    } else if (result && command.id === invoiceCommand.setDefermentDate) {
      this.dataContext.defermentDate = this.commandContext.defermentDate;
      const form = this.form.controls['defermentDate'] as FormGroup;
      form.controls['year'].setValue(this.commandContext.defermentDate.year);
      form.controls['month'].setValue(this.commandContext.defermentDate.month);
      form.controls['day'].setValue(this.commandContext.defermentDate.day);
    } else super.executedWithResult(result, command);
  }

  get hasDeliveryMethodEmail(): boolean {
    const value = this.form.controls['deliveryMethod'].value;
    return value === InvoiceDeliveryMethod.EmailWithPDF || value === InvoiceDeliveryMethod.EmailWithoutPDF;
  }

  private _getPath(category: LeaseAgreementCategory) {
    let segment = '';
    switch (category) {
      case LeaseAgreementCategory.Block:
      case LeaseAgreementCategory.Dwelling:
      case LeaseAgreementCategory.Parking:
      case LeaseAgreementCategory.Premises:
        segment = LeaseAgreementCategory[category].toLowerCase();
        break;

      case LeaseAgreementCategory.Other:
        segment = 'other-rental-object';
        break;

      case LeaseAgreementCategory.CooperativeHousing:
        segment = 'co-housing';
        break;

      default:
        throw new Error(`The specified contract category is not supported. Category: ${category}`);
    }
    return segment;
  }

  onLeaseAgreementRowClicked(e: { context: Contract; event: any }): void {
    this.navigation.navigate([this._getPath(e.context.category), 'contracts', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  onCreditInvoiceRowClicked(e: { context: CreditInvoice; event: any }): void {
    this.navigation.navigate(['invoices', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  onReminderInvoiceRowClicked(e: { context: InvoiceReminder; event: any }): void {
    const tab = this._nativeWindow.openTab();
    this._dataService.getReminderPdf(e.context.id).subscribe(res => this._nativeWindow.openFile(res, `${e.context.id}.pdf`, tab));
  }

  onContactRowClicked(e: { context: InvoiceContact; event: any }): void {
    this.navigation.navigate(['tenants', 'edit', e.context.contactId], null, e.event.ctrlKey);
  }

  onLogsStateChanged(show: boolean): void {
    if (show) {
      this.allowSectionInit('logs');
    }
  }

  override initializeSection(section: string) {
    switch (section) {
      case 'logs':
        this.loadLogs(this.dataContext.id);
        this.loadVerificationLogs(this.dataContext.id);
    }
    super.initializeSection(section);
  }

  private loadLogs(invoiceId: string): void {
    this.logLoadSubscription?.unsubscribe();
    this.logs = [];
    this.logLoadSubscription = this.logService.getPagedList(invoiceId, 1, 500).subscribe({
      next: response => {
        if (this.dataContext?.id === invoiceId) {
          this.logs = response.items;
        }
      },
      error: error => {
        if (this.dataContext?.id === invoiceId) {
          this.messageService.showError(error);
        }
      },
    });
    this.AddSubscription(this.logLoadSubscription);
  }

  private loadVerificationLogs(invoiceId: string): void {
    this.verificationLogLoadSubscription?.unsubscribe();
    this.verificationLogs = [];
    this.verificationLogLoadSubscription = this.verificationLogService.getPagedList(invoiceId, 1, 500).subscribe({
      next: response => {
        if (this.dataContext?.id === invoiceId) {
          this.verificationLogs = response.items;
        }
      },
      error: error => {
        if (this.dataContext?.id === invoiceId) {
          this.verificationLogs = [];
          this.initializedSections = this.initializedSections.filter(section => section !== 'logs');
          this.messageService.showError(error);
        }
      },
    });
    this.AddSubscription(this.verificationLogLoadSubscription);
  }

  protected prepareSave() {
    const model = super.prepareSave();
    // Disabled controls are omitted from form.value. External invoices keep the delivery
    // method disabled in the UI, but the immutable value must still be included in the request.
    model.deliveryMethod = this.form.controls['deliveryMethod'].value;
    model.debtor.address = Object.assign(new PostalAddress(), this.form.value.recipientAddress);
    model.debtor.email = this.form.value.debtorEmailAddress;
    model.invoiceRecipientEmail = this.form.value.debtorEmailAddress;
    model.defermentDate = this.dataContext.defermentDate;
    model.refersToPeriod = this.dataContext.refersToPeriod;
    model.invoiceDate = this.dataContext.invoiceDate;
    model.currencyCode = this.dataContext.currencyCode;
    model.id = this.dataContext.id;
    model.invoiceRowsChangeModel = {
      changeHappened: this.hasInvoiceRowsChanged,
      amountAndTextRows: this.dataContext.amountAndTextRows,
      changedInvoiceRowsIds: Array.from(this.changedIds),
    };
    model.newAppendixes = this.appendixes.controls.map(f => f.value.data);

    return model;
  }

  onSubmit() {
    if (!this.canManageInvoices) {
      return;
    }

    if (this.hasInvoiceRowsChanged) {
      if (this.dataContext.amountAndTextRows.every(ir => !ir.amount?.amount)) {
        this.messageService.showError('INVOICE.ERROR_TEXT.NO_AMOUNT_ROW');
        return;
      }

      if (this.dataContext.isCreditInvoice != this.form.controls['amount'].value.amount < 0) {
        this.messageService.showError('INVOICE.ERROR_TEXT.CANT_CHANGE_NEGATIVE_TO_POSITIVE');
        return;
      }
    }

    super.onSubmit();
  }

  public isAttested: boolean;

  protected getFormGroup(): FormGroup {
    this.isAttested = this.dataContext.attestedDate !== undefined && this.dataContext.attestedDate !== null;
    return this._fb.group({
      netAmount: this._fc.createReadonly('INVOICE.PREVIEW.AMOUNT', this.dataContext.netAmount),
      vatAmount: this._fc.createReadonly('INVOICE.PREVIEW.VAT', this.dataContext.vatAmount),
      rounding: this._fc.createReadonly('INVOICE.PREVIEW.ROUNDING', this.getRoundingValue(this.dataContext.rounding)),
      amount: this._fc.createReadonly('INVOICE.PREVIEW.TOTAL_AMOUNT', this.dataContext.amount),
      remainingAmount: this._fc.createReadonly('INVOICE.REMAINING_AMOUNT', this.dataContext.remainingAmount),
      recipientAddress: this._fb.group({}),
      deliveryMethod: this._fc.create('LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD', this.dataContext.deliveryMethod),
      invoiceDate: this._fb.group({}),
      defermentDate: this._fb.group({}),
      dueDate: this._fb.group({}),
      debtorEmailAddress: this._fc.create('COMMON.EMAIL', this.dataContext.debtor.email),
      invoiceRecipientEmail: this._fc.create('COMMON.EMAIL', this.dataContext.debtor.email),
      invoiceNumber: this._fc.create('INVOICE.INVOICE_NUMBER', this.dataContext.invoiceNumber),
      invoiceRecipient: this._fc.create('INVOICE.RECIPIENT_NAME', this.dataContext.debtor.name),
      invoiceRecipientContactId: this._fc.createHidden(this.dataContext.invoiceRecipientContactId),
      attestedDate: this._fc.create('CONST.INVOICE_STATUS_ATTESTED', this.dataContext.attestedDate),
      labeling: this._fc.createMaxLength('LEASE_AGREEMENT.LABELING', this.dataContext.labeling, 100),
      sentToDebtCollection: this._fb.group({}),
      fullyRegulated: this._fb.group({}),
      periodFrom: this._fb.group({}),
      periodTo: this._fb.group({}),
      note: this._fc.createMaxLength('COMMON.NOTE', this.dataContext.note, 200),
      appendixes: this._fb.array([]),
    });
  }

  get appendixes(): FormArray {
    return this.form.controls['appendixes'] as FormArray;
  }

  get canUploadAppendixes(): boolean {
    return this.canManageInvoices && !this.dataContext.isNew && !this.isAttested;
  }

  showCreditInvoices: boolean;
  showDeliveries: boolean;
  showB2CInvoiceImportEvents: boolean;
  showLeaseAgreements: boolean;
  showPayments: boolean;
  showReminders: boolean;
  showVerifications: boolean;
  showDirectDebits: boolean;
  showDebtCollectionLog: boolean;
  showLabeling: boolean;
  showReadOnlyLabeling: boolean;
  showAppendixes: boolean;
  canManageInvoices: boolean;

  private _verificationsLoaded = false;
  setVerificationsVisibility(show: boolean) {
    if (this._verificationsLoaded) {
      return;
    }
    this._verificationsLoaded = true;
    this.AddSubscription(
      this._dataService.getVerificationsWithBalance(this.dataContext.id).subscribe(v => {
        this.dataContext.accountingTransactions = v.verifications;
        this.dataContext.accountingBalances = v.balances;
      })
    );
  }

  protected init(data: Invoice) {
    const shouldReloadLogs = this.dataContext?.id !== data?.id && this.isSectionAllowed('logs');
    if (shouldReloadLogs) {
      this.logLoadSubscription?.unsubscribe();
      this.verificationLogLoadSubscription?.unsubscribe();
      this.logs = [];
      this.verificationLogs = [];
      this.initializedSections = this.initializedSections.filter(section => section !== 'logs');
    }

    super.init(data);

    if (shouldReloadLogs) {
      this.allowSectionInit('logs');
    }

    if (!this.canManageInvoices) {
      this.form.disable({ emitEvent: false });
    }
    this.AddSubscription(this._dimensionLabelsService.getForCompany(data.companyId).subscribe(labels => this.applyDimensionLabels(labels)));
    this.showLeaseAgreements = data.leaseAgreements && data.leaseAgreements.length > 0;
    this.showVerifications = data.attestedDate ? true : false;
    this.showReminders = data.reminders && data.reminders.length > 0;
    this.showPayments = data.transactions && data.transactions.length > 0;
    this.showCreditInvoices = data.hasCreditInvoices;
    this.showDeliveries = data.deliveries && data.deliveries.length > 0;
    this.showB2CInvoiceImportEvents = data.b2CInvoiceImportEvents && data.b2CInvoiceImportEvents.length > 0;
    this.showDirectDebits = data.directDebits && data.directDebits.length > 0;
    this.showDebtCollectionLog = data.debtCollectionLogs && data.debtCollectionLogs.length > 0;
    this.invoiceDeliveryMethods = data.deliveryMethods.map(
      m => new SelectionItem(InvoiceDeliveryMethodToString(m), m, null, null, GetInvoiceDeliveryMethodIcon(m))
    );
    if (data.deliveryMethod === InvoiceDeliveryMethod.ExternalAccountsReceivable) {
      this.form.controls['deliveryMethod'].disable({ emitEvent: false });
    }
    this.showLabeling = data.typeOfInvoice !== InvoiceType.Rent;
    this.showReadOnlyLabeling = data.labeling !== undefined && data.isAttested;
    this.showAppendixes = data.appendixes && data.appendixes.length > 0;
    if (this.showAppendixes) {
      this.documents = data.appendixes;
    }

    if (!data.deliveryMethods.some(x => x === data.deliveryMethod)) {
      // delivery method was set to a value that is no longer a valid option - so add it as a disabled option:
      this.invoiceDeliveryMethods.push(
        new SelectionItem(
          InvoiceDeliveryMethodToString(data.deliveryMethod),
          data.deliveryMethod,
          true,
          null,
          GetInvoiceDeliveryMethodIcon(data.deliveryMethod)
        )
      );
    }

    this.createRowsListDataContext();
  }

  rowsListDataContext: InvoiceRowListContext;
  private createRowsListDataContext() {
    this.rowsListDataContext = {
      ...this.dataContext,
      amountAndTextRows: this.dataContext.amountAndTextRows,
      codingDefaultValues: {
        counterpart: this.dataContext.invoiceContacts?.find(ic => ic.role == 0)?.contactCounterpart,
      },
    };
  }

  innerFormDirty(): boolean {
    // The API can fail before the base class creates the form. Leaving the route
    // must still be possible in that state.
    return this.hasInvoiceRowsChanged || this.appendixes?.dirty || false;
  }

  invoiceRowCreated(row: InvoiceRow) {
    this.hasInvoiceRowsChanged = true;
    this.updateSum();
  }

  private changedIds: Set<string> = new Set<string>();
  invoiceRowChanged(row: InvoiceRow) {
    if (row?.id) {
      this.hasInvoiceRowsChanged = true;
      this.changedIds.add(row.id);
      this.updateSum();
    }
  }

  invoiceRowDeleted(id: string) {
    if (id) {
      this.hasInvoiceRowsChanged = true;
      this.updateSum();
    }
  }

  invoiceRowReordered() {
    this.hasInvoiceRowsChanged = true;
  }

  formSubmitted() {
    super.formSubmitted();

    if (this.hasInvoiceRowsChanged) {
      this.hasInvoiceRowsChanged = false;
      this.navigation.reloadCurrentRoute();
    }
  }

  formSubmittedNew() {
    super.formSubmitted();

    if (this.hasInvoiceRowsChanged) {
      this.hasInvoiceRowsChanged = false;
      this.navigation.reloadCurrentRoute();
    }
  }

  private getRoundingValue(roundingOnInvoice: Money): Money {
    if (this.dataContext.isCreditInvoice) {
      var roundingRow = this.dataContext.amountAndTextRows.find(r => r.isRounding);
      if (roundingRow) return roundingRow.amount;
    }

    return roundingOnInvoice;
  }

  private updateSum() {
    if (!this.dataContext.allowEditInvoiceRows) {
      return;
    }

    let amounts = this.invoiceAmountService.calculateAmounts(this.dataContext.amountAndTextRows, this.dataContext.currencyCode);

    this.form.controls['netAmount'].setValue(amounts.netAmount);
    this.form.controls['vatAmount'].setValue(amounts.vatAmount);
    this.form.controls['amount'].setValue(amounts.amount);
    this.form.controls['remainingAmount'].setValue(amounts.remainingAmount);
    this.form.controls['rounding'].setValue(this.getRoundingValue(amounts.rounding));
  }

  private applyDimensionLabels(labels: DimensionLabels) {
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingsDefinition, 'costPlace', labels.costPlaceLabel);
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingsDefinition, 'costBearer', labels.costBearerLabel);
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingsDefinition, 'counterpart', labels.counterpartLabel);
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingsDefinition, 'function', labels.functionLabel);
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingsDefinition, 'project', labels.projectLabel);
    this.columnAccountingsDefinition = [...this.columnAccountingsDefinition];
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingBalance, 'costPlace', labels.costPlaceLabel);
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingBalance, 'costBearer', labels.costBearerLabel);
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingBalance, 'counterpart', labels.counterpartLabel);
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingBalance, 'function', labels.functionLabel);
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingBalance, 'project', labels.projectLabel);
    this.columnAccountingBalance = [...this.columnAccountingBalance];
  }
}
